import type React from "react";
import { AlertCircle, RefreshCw, Loader2, WifiOff } from "lucide-react";
import { Button } from "@planta-v2/pds-ui/button";
import { applicationLogger } from "@frontend/shared/infrastructure/logger/application-logger.singleton";
import { i18n } from "@frontend/i18n";

interface DirectoryErrorStateProps {
  readonly onRetry: () => void;
  readonly error?: Error | null;
  readonly isRetrying?: boolean;
}

function isNetworkError(error: Error | null | undefined): boolean {
  if (error === null || error === undefined) {
    return false;
  }
  const message = error.message.toLowerCase();
  return (
    message.includes("network") ||
    message.includes("fetch") ||
    message.includes("connection") ||
    error.name === "TypeError"
  );
}

export function DirectoryErrorState({
  onRetry,
  error,
  isRetrying = false,
}: DirectoryErrorStateProps): React.JSX.Element {
  if (error !== null && error !== undefined) {
    applicationLogger.error(
      "Directory error state rendered",
      "DirectoryErrorState",
      error,
    );
  }

  const isNetwork = isNetworkError(error);
  const Icon = isNetwork ? WifiOff : AlertCircle;

  const title = isNetwork
    ? i18n.t("user.directory.error.networkTitle")
    : i18n.t("user.directory.error.title");

  const description = isNetwork
    ? i18n.t("user.directory.error.networkDescription")
    : i18n.t("user.directory.error.description");

  return (
    <div
      className="flex flex-col items-center justify-center py-12 px-4"
      data-testid="directory-error-state"
    >
      <Icon className="h-12 w-12 text-destructive/50 mb-4" />
      <h3 className="font-serif text-base font-normal text-foreground mb-2.5">
        {title}
      </h3>
      <p className="text-pds-2xs leading-relaxed text-muted-foreground text-center mb-4">
        {description}
      </p>
      <Button
        variant="outline"
        onClick={onRetry}
        disabled={isRetrying}
        className="gap-2"
        data-testid="directory-error-retry-button"
      >
        {isRetrying ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <RefreshCw className="h-4 w-4" />
        )}
        {isRetrying
          ? i18n.t("user.directory.error.retrying")
          : i18n.t("user.directory.error.retryButton")}
      </Button>
    </div>
  );
}
